import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import moment from 'moment'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import useHasMounted from 'hooks/useHasMounted'
import useCountdownTagOptions from 'home/hooks/useCountdownTagOptions'

const FixedWidthCountdown = styled.div`
  display: inline-block;

  &.fixed-width {
    width: 7ch;
  }
`

export type TagPlace = 'offer-card' | 'offer-main' | 'offer-gallery' | 'offer-search-tile' | 'deals-tile-item' | 'offer-summary-card' | 'bedbank-offer-card' | 'offer-label' | 'tour-v2-urgency-tags' | 'bedbank-offer-intro' | 'common-labels' | 'tour-offer-card' | 'offer-carousel-card' | 'flight-deal-content' | 'cruise-content' | 'cruise-deal-hero' | 'cruise-urgency-label' | 'carousel-card-medium' | 'offer-labels' | 'lux-plus-offer-label' | 'tour-deal-hero'

interface Props {
  className?: string
  overrideShowWithinXDays?: number
  endDate: string
  showHourOnly?: boolean
  isLuxPlusEarlyAccess?: boolean
  prefixLabel?: string
  tagPlace: TagPlace
  fallback?: React.ReactElement
}

function OfferUrgencyCountdownLabel(props: Props) {
  const {
    endDate,
    overrideShowWithinXDays,
    isLuxPlusEarlyAccess = false,
    prefixLabel,
    showHourOnly,
    tagPlace,
    fallback = null,
    ...rest
  } = props
  const intl = useIntl()
  const [now, setNow] = useState<moment.Moment>(moment())
  const luxPlusEnabled = useAppSelector(state => getIsLuxPlusEnabled(state))
  const countdownOptions = useCountdownTagOptions({
    tagPlaceId: tagPlace,
    overrideShowWithinXDays,
  })

  const hasMounted = useHasMounted()

  const {
    message,
    timeLeft,
  } = useMemo(() => {
    const end = moment(endDate)

    const leftToTheEnd = moment.duration(end.diff(now))
    const roundedEndDays = Math.floor(leftToTheEnd.asDays())
    const endHours = leftToTheEnd.asHours()

    const baseMessage = prefixLabel
      ? intl.formatMessage(
          {
            id: 'Hd7lxlng+j',
            defaultMessage: '{prefixLabel} ends in',
            description: 'Countdown label prefix with custom offer type ending soon',
          },
          { prefixLabel },
        )
      : intl.formatMessage({
          id: 'PrgYnbFBUh',
          defaultMessage: 'Ends in',
          description: 'Countdown label prefix for offer ending soon',
        })
    const message = isLuxPlusEarlyAccess && luxPlusEnabled
      ? intl.formatMessage({
          id: 'csYl+mguC1',
          defaultMessage: 'Priority access ends in',
          description: 'Countdown label prefix for LuxPlus early access period ending',
        })
      : baseMessage

    if (endHours == 0) {
      return {
        message: undefined,
        timeLeft: undefined,
      }
    }

    if (endHours > 24) {
      const daysLeftLabel = intl.formatMessage(
        {
          id: 'BOUhFx3rUi',
          defaultMessage: '{count, plural, one {# day} other {# days}}',
          description: 'Number of days remaining in countdown urgency label',
        },
        { count: roundedEndDays },
      )

      if (isLuxPlusEarlyAccess && luxPlusEnabled) {
        return {
          message,
          timeLeft: daysLeftLabel,
        }
      }

      if (roundedEndDays <= countdownOptions.showWithinXDays) {
        return {
          message: baseMessage,
          timeLeft: daysLeftLabel,
        }
      }
    }

    if (endHours <= 24 && leftToTheEnd.asSeconds() > 0) {
      const timeLeft = moment.utc(leftToTheEnd.asMilliseconds())
      // round up the hour
      if (showHourOnly) timeLeft.add({ minutes: 59, seconds: 59 })

      const timeLeftString = showHourOnly ?
          timeLeft.format('HH') :
          timeLeft.format('HH:mm:ss')

      return {
        message,
        timeLeft: <FixedWidthCountdown className={clsx({ 'fixed-width': !showHourOnly })}>
          {timeLeftString}
        </FixedWidthCountdown>,
      }
    }

    return {
      message: undefined,
      timeLeft: undefined,
    }
  }, [intl, endDate, now, prefixLabel, isLuxPlusEarlyAccess, luxPlusEnabled, countdownOptions.showWithinXDays, showHourOnly])

  // only update per minute if we only show hr
  const updateInterval = showHourOnly ? 60000 : 1000

  // Only run the interval if message is truthy (countdown is visible)
  React.useEffect(() => {
    if (!message) return
    const id = setInterval(() => setNow(moment()), updateInterval)
    return () => clearInterval(id)
  }, [message, updateInterval])

  if (!message) {
    return fallback ?? null
  }

  return (
    <UrgencyLabel
      {...rest}
      data-tag-place-id={tagPlace}
      className={clsx(rest.className, { 'not-mounted': !hasMounted })}
      variant="critical2"
    >
      {message} {hasMounted && timeLeft}
    </UrgencyLabel>
  )
}

export default OfferUrgencyCountdownLabel
